import { useTranslation } from 'react-i18next';

export interface BillApplicationFormValue {
  bill_id: string;
  amount_applied: string; // bigint minor units as string
}

export interface OpenBill {
  id: string;
  number: string;
  entry_date: string;
  grand_total: string;
  paid_amount: string;
}

export interface BillApplicationRowProps {
  index: number;
  value: BillApplicationFormValue;
  openBills: OpenBill[];
  canRemove: boolean;
  onChange: (v: BillApplicationFormValue) => void;
  onRemove: () => void;
}

export function BillApplicationRow({
  value,
  openBills,
  canRemove,
  onChange,
  onRemove,
}: BillApplicationRowProps) {
  const { t } = useTranslation('bookkeeping');

  const selectedBill = openBills.find((b) => b.id === value.bill_id);

  // Compute remaining balance on the selected bill after applying this amount
  let remaining = '—';
  if (selectedBill) {
    try {
      const grandTotal = BigInt(selectedBill.grand_total);
      const paidAmount = BigInt(selectedBill.paid_amount);
      const openBalance = grandTotal - paidAmount;
      const applied = value.amount_applied ? BigInt(value.amount_applied) : 0n;
      remaining = (openBalance - applied).toString();
    } catch {
      remaining = '—';
    }
  }

  function handleBillChange(billId: string) {
    const bill = openBills.find((b) => b.id === billId);
    if (bill) {
      // Default amount_applied = open balance
      let openBalance = '';
      try {
        openBalance = (BigInt(bill.grand_total) - BigInt(bill.paid_amount)).toString();
      } catch {
        openBalance = '';
      }
      onChange({ ...value, bill_id: billId, amount_applied: openBalance });
    } else {
      onChange({ ...value, bill_id: billId, amount_applied: '' });
    }
  }

  return (
    <tr className="border-b">
      {/* Bill picker */}
      <td className="px-2 py-1">
        <select
          value={value.bill_id}
          onChange={(e) => handleBillChange(e.target.value)}
          className="w-full min-w-40 rounded border px-1 py-0.5 text-sm"
        >
          <option value="">— {t('purchases.bills.title')}</option>
          {openBills.map((b) => (
            <option key={b.id} value={b.id}>
              {b.number} ({b.entry_date})
            </option>
          ))}
        </select>
      </td>
      {/* Open balance */}
      <td className="px-2 py-1 text-right font-mono text-xs text-slate-500">
        {selectedBill
          ? (() => {
              try {
                return (
                  BigInt(selectedBill.grand_total) - BigInt(selectedBill.paid_amount)
                ).toString();
              } catch {
                return '—';
              }
            })()
          : '—'}
      </td>
      {/* Amount applied */}
      <td className="px-2 py-1">
        <input
          value={value.amount_applied}
          onChange={(e) => onChange({ ...value, amount_applied: e.target.value })}
          placeholder="0"
          className="w-28 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      {/* Remaining after application */}
      <td className="px-2 py-1 text-right font-mono text-xs text-slate-500">{remaining}</td>
      {/* Remove */}
      <td className="px-2 py-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30"
          title="Eliminar aplicación"
        >
          ×
        </button>
      </td>
    </tr>
  );
}
